import { memo, useState, useContext, useCallback } from 'react';

import { InjectedIntl, injectIntl } from 'react-intl';

import { Profile } from '@atlaskit/atlassian-navigation';
import Avatar from '@atlaskit/avatar';
import { Popup } from '@atlaskit/popup';

import AccountMenu from '../account-menu';
import messages from '../account-menu.i18n';
import { ThemeSelectorContext } from '../theme-selector/theme-selector';
import {
  ThemeFeedbackCollectorContext,
  ThemeSelectorFeedbackCollector,
  ThemeSelectorFeedbackSuccessFlag,
} from '../theme-selector/theme-selector-feedback';

import { HorizontalNavContext } from './horizontal-nav-context';

type Props = {
  intl: InjectedIntl;
};

const HorizontalProfilePopup = (props: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { avatarUrl } = useContext(HorizontalNavContext);

  // relevant state for dark theme selector feedback experience
  const [isThemeFeedbackModalOpen, setIsThemeFeedbackCollectorModalOpen] =
    useState(false);
  const [isFlagOpen, setIsFlagOpen] = useState(false);

  const onClick = () => {
    setIsOpen(!isOpen);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const accountMenuProps = useContext(HorizontalNavContext);

  const tooltip = accountMenuProps.isLoggedIn
    ? messages.loggedInUserTooltip
    : messages.anonymousUserTooltip;

  const popupContent = useCallback(() => {
    const themeSelectorProps = {
      onThemeSelect: onClose,
    };

    return (
      <>
        <ThemeSelectorContext.Provider
          value={{
            setFeedbackModalOpen: (value: boolean) => {
              setIsThemeFeedbackCollectorModalOpen(value);
              setIsFlagOpen(false);
              onClose();
            },
            publishUiEvent: accountMenuProps.publishUiEvent,
          }}
        >
          <AccountMenu
            {...accountMenuProps}
            themeSelectorProps={themeSelectorProps}
            setHorizontalProfilePopupClose={onClose}
          />
        </ThemeSelectorContext.Provider>
      </>
    );
  }, [accountMenuProps]);

  return (
    <>
      <Popup
        placement="bottom-start"
        content={popupContent}
        isOpen={isOpen}
        onClose={onClose}
        trigger={triggerProps => (
          <Profile
            icon={<Avatar size="small" src={avatarUrl} />}
            onClick={onClick}
            isSelected={isOpen}
            tooltip={props.intl.formatMessage(tooltip)}
            testId="profile-button"
            {...triggerProps}
          />
        )}
      />
      <>
        <ThemeFeedbackCollectorContext.Provider
          value={{
            userEmail: accountMenuProps.userEmail || undefined,
            userDisplayName: accountMenuProps.userDisplayName,
            userAtlassianAccountId: accountMenuProps.currentUser?.account_id,
            isFeedbackModalOpen: isThemeFeedbackModalOpen,
            setFeedbackModalOpen: (value: boolean) => {
              setIsThemeFeedbackCollectorModalOpen(value);
            },
            onFeedbackSubmit: () => setIsFlagOpen(true),
            intl: props.intl,
          }}
        >
          <ThemeSelectorFeedbackCollector />
        </ThemeFeedbackCollectorContext.Provider>
        <ThemeSelectorFeedbackSuccessFlag
          intl={props.intl}
          onClose={() => setIsFlagOpen(false)}
          isOpen={isFlagOpen}
        />
      </>
    </>
  );
};

export default memo(injectIntl(HorizontalProfilePopup));
